/**
 * Demand mode filter — set semantics (spec §5.1).
 *
 * The filter went from all-or-one (`null` = all, a string = that one) to a set.
 * Two invariants make the rest of the base layer legible:
 *
 *   1. Canonical order. Modes are always returned ROAD, RAIL, SEA, AIR, so the
 *      filter buttons, the persisted value and the layer's draw order agree.
 *   2. Never empty. Toggling off the last active mode is a no-op, so "nothing is
 *      drawn" has exactly one cause — the demand overlay being off.
 *
 * Pure module: no Vue, no Leaflet, no storage.
 */

/** Canonical mode order. Mirrors world.transigo.common.domain.TransportMode. */
export const ALL_MODES = ['ROAD', 'RAIL', 'SEA', 'AIR']

/**
 * Spec §5.1: "Default: single mode active." ROAD is first in canonical order and
 * present in every scenario's seed data.
 */
export const DEFAULT_MODES = ['ROAD']

/**
 * Coerces any stored, prop-supplied or legacy value into a valid active set.
 *
 * @param {string[]|string|null|undefined} value
 * @returns {string[]} a fresh array, canonically ordered, never empty
 */
export function normalizeModes(value) {
  if (typeof value === 'string') {
    return ALL_MODES.includes(value) ? [value] : [...DEFAULT_MODES]
  }
  if (!Array.isArray(value)) return [...DEFAULT_MODES]

  const kept = ALL_MODES.filter(m => value.includes(m))
  return kept.length > 0 ? kept : [...DEFAULT_MODES]
}

/**
 * Adds or removes one mode. Removing the last active mode is a no-op.
 *
 * @param {string[]|string|null} active
 * @param {string} mode
 * @returns {string[]} a fresh array
 */
export function toggleMode(active, mode) {
  const current = normalizeModes(active)
  if (!ALL_MODES.includes(mode)) return current

  if (current.includes(mode)) {
    if (current.length === 1) return current
    return current.filter(m => m !== mode)
  }
  return ALL_MODES.filter(m => current.includes(m) || m === mode)
}

/** @returns {string[]} every mode, as a fresh array. */
export function selectAllModes() {
  return [...ALL_MODES]
}
